import { Chip, Paper, Switch, Tooltip, Typography } from '@mui/material'

import { Box } from '@/components/ui'
import {
  getOrgFeatureStateLabel,
  isOrgFeatureMovable,
  isOrgFeatureOn,
  type OrgFeatureState,
  UNMOVABLE_ORG_FEATURE_REASON,
} from '@/definitions/featureFlags'

export interface OrgFeatureFlagRowProps {
  name: string
  description: string
  state: OrgFeatureState
  /** Where the operator has moved the switch, which may differ from `state`. */
  staged: boolean
  onStage: (staged: boolean) => void
}

/**
 * One flag and whether this practice has it.
 *
 * A flag granted to this practice can be switched either way. Two shapes cannot:
 * one granted by the flag's own "on for all orgs" switch, which gets a plain
 * statement in place of a control because there is nothing here to operate, and
 * one whose row carries a window somebody set on purpose, which keeps a locked
 * switch and says why.
 */
export const OrgFeatureFlagRow = ({
  name,
  description,
  state,
  staged,
  onStage,
}: OrgFeatureFlagRowProps) => {
  const isGlobal = state === 'ALL_ORGS'
  const isMovable = isOrgFeatureMovable(state)
  const lockReason = UNMOVABLE_ORG_FEATURE_REASON[state]
  const isOn = isOrgFeatureOn(state)
  // A locked row reports what is stored; a movable one follows the operator.
  const checked = isMovable ? staged : isOn
  const isChanged = isMovable && staged !== isOn

  return (
    <Paper variant='infoRow'>
      <Box variant='flexBetween'>
        <Box variant='flexColumn' gap='xs' fullWidth={false} minWidth={0}>
          <Box variant='flex' gap='sm' fullWidth={false}>
            <Typography variant='subtitle2'>{name}</Typography>
            <Chip
              size='small'
              label={getOrgFeatureStateLabel(state)}
              color={isOn ? 'success' : 'default'}
            />
            {isChanged && (
              <Chip
                size='small'
                variant='outlined'
                color='warning'
                label={staged ? 'Will turn on' : 'Will turn off'}
              />
            )}
          </Box>
          <Typography variant='caption' color='text.secondary'>
            {description}
          </Typography>
          {/* On the row rather than only in the tooltip: a hover hides the one
              thing the operator needs in order to know what to do next. */}
          {lockReason !== undefined && (
            <Typography variant='caption' color='warning.main'>
              {lockReason}
            </Typography>
          )}
        </Box>

        {isGlobal ? (
          <Typography variant='caption' color='text.secondary' noWrap>
            Globally Enabled
          </Typography>
        ) : (
          // A disabled switch swallows pointer events, so the reason goes on a
          // wrapper rather than on the control.
          <Tooltip title={lockReason ?? ''} placement='top-end'>
            <span>
              <Switch
                checked={checked}
                disabled={!isMovable}
                slotProps={{ input: { 'aria-label': name } }}
                onChange={(event) => onStage(event.target.checked)}
              />
            </span>
          </Tooltip>
        )}
      </Box>
    </Paper>
  )
}
